import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, formatApiError } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";
import {
  MagnifyingGlass,
  Bank,
  MapPin,
  CreditCard,
  Buildings,
  ArrowUpRight,
  CheckCircle,
  Clock,
  Warning,
} from "@phosphor-icons/react";

const TOOL_CARDS = [
  {
    icon: Bank,
    title: "Federal Refund Status",
    desc: "Check the IRS 'Where's My Refund?' tool for your federal return.",
    cta: "Check on IRS.gov",
    href: "https://sa.www4.irs.gov/wmr/",
    accent: "text-[#0A2540] bg-[#0A2540]/5",
  },
  {
    icon: MapPin,
    title: "State Refund Status",
    desc: "Jump directly to your state's Department of Revenue portal.",
    cta: "View states",
    hash: "#state-list",
    accent: "text-[#D32F2F] bg-[#D32F2F]/10",
  },
  {
    icon: CreditCard,
    title: "Pay Federal Tax Due",
    desc: "Direct Pay, EFTPS, debit or credit card via the official IRS site.",
    cta: "Pay on IRS.gov",
    href: "https://www.irs.gov/payments",
    accent: "text-[#0A2540] bg-[#0A2540]/5",
  },
  {
    icon: Buildings,
    title: "Pay State Tax Due",
    desc: "Use ACI Payments (Official Payments) to settle your state balance.",
    cta: "Pay state",
    href: "https://www.acipayonline.com/",
    accent: "text-[#D32F2F] bg-[#D32F2F]/10",
  },
];

const STATES = [
  ["Alabama", "https://myalabamataxes.alabama.gov/_/"],
  ["Alaska", null],
  ["Arizona", "https://aztaxes.gov/Home/CheckRefund"],
  ["Arkansas", "https://atap.arkansas.gov/_/"],
  ["California", "https://webapp.ftb.ca.gov/refund/login"],
  ["Colorado", "https://www.colorado.gov/revenueonline/_/"],
  ["Connecticut", "https://drsindtax.ct.gov/AUT/welcomeindividual.aspx"],
  ["Delaware", "https://dorweb.revenue.delaware.gov/scripts/refinq/refinq.dll"],
  ["Florida", null],
  ["Georgia", "https://gtc.dor.ga.gov/_/"],
  ["Hawaii", "https://tax.ehawaii.gov/hoihoi/refund.html"],
  ["Idaho", "https://idahotap.gentax.com/TAP/_/"],
  ["Illinois", "https://mytax.illinois.gov/_/"],
  ["Indiana", "https://secure.in.gov/apps/dor/tax/refund/"],
  ["Iowa", "https://tax.iowa.gov/wheres-my-refund"],
  ["Kansas", "https://www.kdor.ks.gov/Apps/kcsc/increfundstatus.aspx"],
  ["Kentucky", "https://iitrefundstatus.ky.gov/TRFWeb/"],
  ["Louisiana", "https://latap.revenue.louisiana.gov/_/"],
  ["Maine", "https://portal.maine.gov/refundstatus/refund"],
  ["Maryland", "https://interactive.marylandtaxes.com/INDIV/refundstatus/home.aspx"],
  ["Massachusetts", "https://www.mass.gov/dor/e-services/masstaxconnect.html"],
  ["Michigan", "https://treas-secure.treas.state.mi.us/eservice_enu/start.swe"],
  ["Minnesota", "https://www.mndor.state.mn.us/tp/refund/_/"],
  ["Mississippi", "https://tap.dor.ms.gov/_/"],
  ["Missouri", "https://mytax.mo.gov/rptp/portal/individual/return-tracker"],
  ["Montana", "https://tap.dor.mt.gov/_/"],
  ["Nebraska", "https://ndr-refundstatus.ne.gov/refundstatus/public/search.faces"],
  ["Nevada", null],
  ["New Hampshire", null],
  ["New Jersey", "https://www16.state.nj.us/TYTR_TGI_INQ/servlet/COMMON/prompt"],
  ["New Mexico", "https://tap.state.nm.us/Tap/_/"],
  ["New York", "https://www8.tax.ny.gov/PRIS/prisStart"],
  ["North Carolina", "https://eservices.dor.nc.gov/wheresmyrefund/refundinput.jsp"],
  ["North Dakota", "https://apps.nd.gov/tax/tap/_/"],
  ["Ohio", "https://tax.ohio.gov/individual/refund-status/check-my-refund-status"],
  ["Oklahoma", "https://oktap.tax.ok.gov/oktap/Web/_/"],
  ["Oregon", "https://revenueonline.dor.oregon.gov/tap/_/"],
  ["Pennsylvania", "https://www.mypath.pa.gov/_/"],
  ["Rhode Island", "https://www.ri.gov/taxation/refund/"],
  ["South Carolina", "https://dor.sc.gov/tax/refund-call"],
  ["South Dakota", null],
  ["Tennessee", null],
  ["Texas", null],
  ["Utah", "https://tap.tax.utah.gov/TaxExpress/_/"],
  ["Vermont", "https://myvtax.vermont.gov/_/"],
  ["Virginia", "https://www.individual.tax.virginia.gov/VTOL/IndRefundStatus.seam"],
  ["Washington", null],
  ["West Virginia", "https://mytaxes.wvtax.gov/_/"],
  ["Wisconsin", "https://ww2.revenue.wi.gov/RefundInquiry/"],
  ["Wyoming", null],
];

const STATUS_STYLES = {
  draft: { label: "Draft", cls: "bg-slate-100 text-slate-700", icon: Clock },
  in_review: { label: "In review", cls: "bg-amber-50 text-amber-700", icon: Clock },
  filed: { label: "E-filed", cls: "bg-[#0A2540]/10 text-[#0A2540]", icon: CheckCircle },
  accepted: { label: "Accepted", cls: "bg-emerald-50 text-emerald-700", icon: CheckCircle },
  rejected: { label: "Rejected", cls: "bg-[#D32F2F]/10 text-[#D32F2F]", icon: Warning },
};

function StatusLookup() {
  const [reference, setReference] = useState("");
  const [ssn, setSsn] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setResult(null);
    if (!reference.trim() || !/^\d{4}$/.test(ssn)) return;
    setLoading(true);
    try {
      const { data } = await api.post(`/returns/lookup`, { reference: reference.trim(), ssn_last4: ssn });
      setResult(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const statusInfo = result && (STATUS_STYLES[result.status] || { label: result.status, cls: "bg-slate-100 text-slate-700", icon: Clock });

  return (
    <div className="rounded-lg border tf-hairline bg-white p-6 lg:p-8" data-testid="lookup-panel">
      <div className="text-xs font-mono uppercase tracking-widest text-[#D32F2F]">Client tool</div>
      <h3 className="mt-2 font-[Chivo] text-2xl lg:text-3xl font-black text-[#0A2540]">Check your filing status</h3>
      <p className="mt-2 text-slate-600 text-sm">Enter your reference number and the last 4 digits of the SSN on file. Both are required to protect your data.</p>
      <form onSubmit={submit} className="mt-5 space-y-3" data-testid="lookup-form">
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder="Reference number (e.g. 8f4b52a1-2c7d-…)"
            className="pl-9 font-mono text-xs h-11"
            data-testid="lookup-input"
          />
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <Input
            value={ssn}
            onChange={(e) => setSsn(e.target.value.replace(/\D/g, "").slice(0, 4))}
            placeholder="Last 4 of SSN"
            inputMode="numeric"
            className="font-mono h-11 sm:w-40 tracking-widest"
            data-testid="lookup-ssn"
          />
          <Button type="submit" disabled={loading || !reference.trim() || ssn.length !== 4} className="bg-[#0A2540] hover:bg-[#0A2540]/90 h-11 px-6 flex-1 sm:flex-none" data-testid="lookup-submit">
            {loading ? "Checking…" : "Check status"}
          </Button>
        </div>
      </form>

      {error && (
        <div className="mt-5 rounded-md border border-[#D32F2F]/20 bg-[#D32F2F]/5 p-4 text-sm text-[#D32F2F]" data-testid="lookup-error">
          {error}
        </div>
      )}

      {result && statusInfo && (
        <div className="mt-6 rounded-md border tf-hairline bg-slate-50 p-5" data-testid="lookup-result">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-xs font-mono uppercase tracking-widest text-slate-500">Tax year {result.tax_year} · {result.return_type}</div>
              <div className="mt-1 font-[Chivo] text-lg font-bold text-[#0A2540]">{result.owner_name}</div>
            </div>
            <span className={`inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest px-3 py-1 rounded ${statusInfo.cls}`}>
              <statusInfo.icon size={12} weight="fill" /> {statusInfo.label}
            </span>
          </div>
          {typeof result.refund_amount === "number" && result.refund_amount > 0 && (
            <div className="mt-4 pt-4 border-t tf-hairline flex justify-between text-sm">
              <span className="text-slate-500">Expected refund</span>
              <span className="font-mono text-emerald-700">${Number(result.refund_amount).toLocaleString()}</span>
            </div>
          )}
          <div className="mt-4 text-xs text-slate-500 font-mono">Last updated {(result.updated_at || "").split("T")[0]}</div>
        </div>
      )}
    </div>
  );
}

function StateCard({ name, url }) {
  const free = url === null;
  return (
    <div className={`rounded-md border tf-hairline p-4 flex items-center justify-between gap-3 ${free ? "bg-emerald-50/40" : "bg-white hover:border-[#0A2540]/40 transition-colors"}`} data-testid={`state-${name.toLowerCase().replace(/\s/g, "-")}`}>
      <div>
        <div className="font-[Chivo] font-bold text-[#0A2540] text-sm">{name}</div>
        {free ? (
          <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-700 mt-1">No state income tax</div>
        ) : (
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mt-1">Refund tracker</div>
        )}
      </div>
      {!free && (
        <a href={url} target="_blank" rel="noreferrer" className="text-xs font-semibold text-[#D32F2F] inline-flex items-center gap-1 shrink-0" data-testid={`state-link-${name.toLowerCase().replace(/\s/g, "-")}`}>
          Open <ArrowUpRight size={12} />
        </a>
      )}
    </div>
  );
}

function ToolCard({ card }) {
  const Icon = card.icon;
  return (
    <div className="h-full rounded-lg border tf-hairline bg-white p-6 flex flex-col hover:-translate-y-0.5 hover:shadow-sm transition-transform group">
      <div className={`h-10 w-10 rounded-md grid place-items-center ${card.accent}`}>
        <Icon size={20} weight="duotone" />
      </div>
      <h3 className="mt-5 font-[Chivo] text-lg font-bold text-[#0A2540]">{card.title}</h3>
      <p className="mt-2 text-sm text-slate-600 leading-relaxed flex-1">{card.desc}</p>
      <div className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-[#D32F2F]">
        {card.cta} <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </div>
    </div>
  );
}

export default function Refunds() {
  const [filter, setFilter] = useState("");
  useSeo({
    title: "Refunds & Payments | Taxfree Filer LLP",
    description: "Check your federal and state tax refund status, pay balances due, and look up your Taxfree Filer LLP filing — all in one place.",
    robots: "index,follow",
    canonical: "https://taxfreefiler.com/refunds",
  });
  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return STATES;
    return STATES.filter(([name]) => name.toLowerCase().includes(q));
  }, [filter]);

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden border-b tf-hairline">
        <div className="absolute inset-0 tf-grid opacity-60" aria-hidden />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-8 py-16 lg:py-20">
          <div className="max-w-3xl">
            <div className="text-xs font-mono uppercase tracking-widest text-[#D32F2F]">Refunds & Payments</div>
            <h1 className="mt-3 font-[Chivo] text-5xl lg:text-6xl font-black text-[#0A2540] tracking-tight" data-testid="refunds-title">
              Check your refund. Pay a balance.
            </h1>
            <p className="mt-5 text-lg text-slate-600 leading-relaxed">
              Every federal and state resource in one place — plus a private lookup for filings we've prepared for you at Taxfree Filer LLP.
            </p>
          </div>
        </div>
      </section>

      {/* TOOL CARDS */}
      <section className="mx-auto max-w-7xl px-6 lg:px-8 py-12 lg:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {TOOL_CARDS.map((c) => {
            const testId = `tool-${c.title.toLowerCase().replace(/\s/g, "-")}`;
            if (c.href) {
              return (
                <a key={c.title} href={c.href} target="_blank" rel="noreferrer" data-testid={testId}>
                  <ToolCard card={c} />
                </a>
              );
            }
            return (
              <a key={c.title} href={c.hash} data-testid={testId}>
                <ToolCard card={c} />
              </a>
            );
          })}
        </div>
      </section>

      {/* LOOKUP + INFO */}
      <section className="mx-auto max-w-7xl px-6 lg:px-8 pb-16 grid lg:grid-cols-5 gap-8">
        <div className="lg:col-span-3">
          <StatusLookup />
        </div>
        <aside className="lg:col-span-2 rounded-lg bg-[#0A2540] text-white p-8">
          <div className="text-xs font-mono uppercase tracking-widest text-[#D32F2F]">Timeline</div>
          <h3 className="mt-2 font-[Chivo] text-2xl font-bold">When to expect your refund</h3>
          <ul className="mt-6 space-y-4 text-sm text-slate-200">
            <li className="flex items-start gap-3"><span className="mt-1 h-1.5 w-1.5 rounded-full bg-[#D32F2F]" /><div><strong className="text-white">21 days</strong> — E-filed federal returns with direct deposit (IRS average).</div></li>
            <li className="flex items-start gap-3"><span className="mt-1 h-1.5 w-1.5 rounded-full bg-[#D32F2F]" /><div><strong className="text-white">6–8 weeks</strong> — Paper-filed federal returns or amended (1040-X) returns.</div></li>
            <li className="flex items-start gap-3"><span className="mt-1 h-1.5 w-1.5 rounded-full bg-[#D32F2F]" /><div><strong className="text-white">Varies</strong> — State refunds. Some states process in 3 days, others in 12 weeks.</div></li>
            <li className="flex items-start gap-3"><span className="mt-1 h-1.5 w-1.5 rounded-full bg-[#D32F2F]" /><div><strong className="text-white">Late Feb+</strong> — Returns claiming EITC or ACTC can't be issued before mid-February by law.</div></li>
          </ul>
          <Link to="/contact"><Button className="mt-8 w-full bg-white text-[#0A2540] hover:bg-slate-100" data-testid="refunds-contact-cta">Talk to a preparer</Button></Link>
        </aside>
      </section>

      {/* STATE GRID */}
      <section id="state-list" className="bg-slate-50 border-y tf-hairline">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 py-16">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="text-xs font-mono uppercase tracking-widest text-[#D32F2F]">State refunds</div>
              <h2 className="mt-2 font-[Chivo] text-3xl lg:text-4xl font-black text-[#0A2540] tracking-tight">Find your state's refund tracker</h2>
              <p className="mt-3 text-slate-600 max-w-2xl">Direct links to every state Department of Revenue. Nine states have no personal income tax — those are flagged in green.</p>
            </div>
            <div className="relative w-full sm:w-72">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter states…" className="pl-9" data-testid="state-filter" />
            </div>
          </div>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="state-grid">
            {filtered.map(([name, url]) => <StateCard key={name} name={name} url={url} />)}
          </div>
          {filtered.length === 0 && (
            <div className="mt-10 text-center text-slate-500" data-testid="state-empty">No states match "{filter}".</div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-6 lg:px-8 py-16">
        <div className="rounded-lg border tf-hairline bg-white p-10 lg:p-14 grid lg:grid-cols-2 gap-8 items-center">
          <div>
            <h2 className="font-[Chivo] text-3xl lg:text-4xl font-black text-[#0A2540] tracking-tight">Still waiting on a refund?</h2>
            <p className="mt-3 text-slate-600 max-w-lg">Sometimes it's a name mismatch, ITIN pending, or an IRS math notice. Our team can identify the root cause in under 24 hours.</p>
          </div>
          <div className="flex flex-wrap gap-3 lg:justify-end">
            <Link to="/services/irs-notice"><Button size="lg" className="bg-[#D32F2F] hover:bg-[#D32F2F]/90 h-11 px-6" data-testid="refunds-notice-cta">IRS notice help</Button></Link>
            <Link to="/contact"><Button size="lg" variant="outline" className="h-11 px-6 border-[#0A2540] text-[#0A2540]">Contact us</Button></Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
